/**
 * MessageComposer (KAN-626)
 *
 * Input panel at the bottom of a client dialog: the operator writes to the
 * client by hand. Enter sends, Shift+Enter inserts a line break. The text is
 * cleared only after a successful send.
 *
 * KAN-688: the paperclip attaches one photo (JPEG, PNG, WEBP up to 10 MB);
 * the text becomes its caption. Sending is possible with text or a photo.
 * KAN-736: `leading` puts extra compact controls (e.g. "Материалы") right
 * after the paperclip, in the same row as the input.
 * KAN-940: with `draftKey` the text is kept as a draft in localStorage
 * (useComposerDraft): restored on open and on a key change, removed after a
 * successful send. The photo is not stored.
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Loader2, Paperclip, Send, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useComposerDraft } from '@/hooks/useComposerDraft'
import { PHOTO_ACCEPT, photoError } from './photoAttachment'

interface MessageComposerProps {
  /** Sends the text (and the photo); resolves true when the message was accepted. */
  onSend: (text: string, photo?: File | null) => Promise<boolean>
  isSending: boolean
  /** Shown above the input (e.g. "the bot may answer too"). */
  hint?: string
  /** Input and send button unavailable (e.g. manual mode is off). */
  disabled?: boolean
  /** Extra controls after the paperclip, left of the input (KAN-736). */
  leading?: ReactNode
  /** KAN-940: key of the text draft (e.g. client:<id>); none = no draft. */
  draftKey?: string
}

/** Object URL of the photo preview, revoked when the photo changes. */
function usePreviewUrl(photo: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!photo) {
      setUrl(null)
      return
    }
    const next = URL.createObjectURL(photo)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [photo])
  return url
}

export function MessageComposer({
  onSend,
  isSending,
  hint,
  disabled = false,
  leading,
  draftKey,
}: MessageComposerProps) {
  const [text, setText] = useComposerDraft(draftKey)
  const [photo, setPhoto] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const previewUrl = usePreviewUrl(photo)
  const canSend =
    !disabled && !isSending && (text.trim().length > 0 || photo !== null)

  const submit = async () => {
    if (!canSend) return
    const ok = photo ? await onSend(text, photo) : await onSend(text)
    if (ok) {
      setText('')
      setPhoto(null)
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void submit()
    }
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    // The same file can be picked again after removal.
    event.target.value = ''
    if (!file) return
    const error = photoError(file)
    if (error) {
      toast.error(error)
      return
    }
    setPhoto(file)
  }

  return (
    <div
      className="shrink-0 space-y-2 border-t border-gray-100 bg-white pt-3"
      data-testid="message-composer"
    >
      {hint && <p className="text-xs text-amber-700">{hint}</p>}
      {photo && previewUrl && (
        <div className="relative inline-block" data-testid="composer-photo-preview">
          <img
            src={previewUrl}
            alt={photo.name}
            className="h-16 w-16 rounded-md border border-gray-200 object-cover"
          />
          <button
            type="button"
            onClick={() => setPhoto(null)}
            aria-label="Убрать фото"
            className="absolute -right-2 -top-2 rounded-full border border-gray-200 bg-white p-0.5 text-gray-600 shadow-sm hover:text-gray-900"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex min-w-0 items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={PHOTO_ACCEPT}
          className="hidden"
          onChange={onPick}
          data-testid="composer-photo-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || isSending}
          aria-label="Прикрепить фото"
          title="Прикрепить фото"
          className="shrink-0"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        {leading}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={photo ? 'Подпись к фото' : 'Сообщение клиенту'}
          aria-label="Сообщение клиенту"
          disabled={disabled}
          rows={2}
          className="min-h-[44px] min-w-0 flex-1 resize-none"
        />
        <Button
          type="button"
          onClick={() => void submit()}
          disabled={!canSend}
          className="shrink-0 gap-1.5"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Отправить
        </Button>
      </div>
    </div>
  )
}
